import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart, Bookmark, BookmarkCheck, Sparkles, Search, ExternalLink, X, ChevronDown, Plus } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import WeeklyPicksCard from '@/components/WeeklyPicksCard';
import ChecklistView from '@/components/ChecklistView';
import VideoPlayer from '@/components/VideoPlayer';

const THEME_COLORS = {
  "Summer": "from-yellow-300 to-orange-400",
  "Wedding": "from-rose-400 to-pink-500",
  "Corporate": "from-slate-400 to-gray-600",
  "Everyday": "from-amber-300 to-orange-400",
  "Night Out": "from-indigo-500 to-purple-600",
  "Festival / Cultural": "from-purple-400 to-pink-500",
  "Glam / Editorial": "from-amber-400 to-red-500",
  "Seasonal": "from-green-300 to-emerald-500",
  "Skincare Journey": "from-teal-300 to-cyan-500",
  "Before & After": "from-violet-400 to-purple-600",
};

export default function SpotlightPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('looks');
  const [looksMode, setLooksMode] = useState('following'); // 'following' or 'discover'
  const [looks, setLooks] = useState([]);
  const [discoverData, setDiscoverData] = useState({ featured: [], looks: [] });
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTheme, setSelectedTheme] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedLook, setSelectedLook] = useState(null);
  const [savedLookIds, setSavedLookIds] = useState(new Set());
  const [savedLooks, setSavedLooks] = useState([]);

  // Define useCallback functions BEFORE useEffect hooks that use them
  const fetchUser = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/auth/me`);
      if (response.ok) setCurrentUser(await response.json());
    } catch (e) { console.error('fetchUser error:', e); }
  }, []);

  const fetchLooks = useCallback(async () => {
    setLoading(true);
    try {
      const url = selectedTheme ? `${API}/looks?theme=${encodeURIComponent(selectedTheme)}` : `${API}/looks`;
      const response = await authFetch(url);
      if (response.ok) setLooks(await response.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedTheme]);

  const fetchDiscover = useCallback(async () => {
    setLoading(true);
    try {
      const url = selectedTheme
        ? `${API}/looks/discover?theme=${encodeURIComponent(selectedTheme)}`
        : `${API}/looks/discover`;
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setDiscoverData(data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedTheme]);

  const fetchTrending = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/analytics/product-usage`);
      if (response.ok) setTrendingProducts(await response.json());
    } catch (e) { console.error('fetchTrending error:', e); }
  }, []);

  const fetchSavedLooks = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/looks/saved`);
      if (response.ok) {
        const saved = await response.json();
        setSavedLooks(saved);
        setSavedLookIds(new Set(saved.map(l => l.look_id)));
      }
    } catch (e) { console.error('fetchSavedLooks error:', e); }
  }, []);

  // useEffect hooks that depend on the useCallback functions above
  useEffect(() => {
    fetchUser();
    fetchLooks();
    fetchTrending();
    fetchSavedLooks();
  }, [fetchUser, fetchLooks, fetchTrending, fetchSavedLooks]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const lookId = params.get('look');
    if (lookId) {
      // Search across all local data sources
      const allLooks = [
        ...looks,
        ...discoverData.featured,
        ...discoverData.looks,
        ...savedLooks
      ];
      const look = allLooks.find(l => l.look_id === lookId);
      if (look) {
        setSelectedLook(look);
      } else if (!loading) {
        // Look not in any local feed — fetch it individually
        authFetch(`${API}/looks/${lookId}`)
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) setSelectedLook(data); })
          .catch(() => {});
      }
    }
  }, [looks, discoverData, savedLooks, location.search, loading]);

  useEffect(() => {
    if (looksMode === 'following') fetchLooks();
    else fetchDiscover();
  }, [selectedTheme, looksMode, fetchLooks, fetchDiscover]);

  useEffect(() => {
    if (activeTab === 'saved') fetchSavedLooks();
  }, [activeTab, fetchSavedLooks]);

  const handleLike = async (lookId) => {
    try {
      const response = await authFetch(`${API}/looks/${lookId}/like`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        const updateLook = (l) => {
          if (l.look_id !== lookId) return l;
          return {
            ...l,
            likes: data.liked
              ? [...(l.likes || []), currentUser?.user_id]
              : (l.likes || []).filter(id => id !== currentUser?.user_id)
          };
        };
        setLooks(prev => prev.map(updateLook));
        setSavedLooks(prev => prev.map(updateLook));
        setDiscoverData(prev => ({
          featured: prev.featured.map(updateLook),
          looks: prev.looks.map(updateLook)
        }));
        if (selectedLook?.look_id === lookId) {
          setSelectedLook(prev => updateLook(prev));
        }
      }
    } catch (e) { toast.error('Failed to like'); }
  };

  const handleSave = async (lookId) => {
    try {
      const response = await authFetch(`${API}/looks/${lookId}/save`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        setSavedLookIds(prev => {
          const next = new Set(prev);
          if (data.saved) next.add(lookId);
          else next.delete(lookId);
          return next;
        });
        // Also update savedLooks array immediately
        if (data.saved) {
          const look = looks.find(l => l.look_id === lookId)
            || discoverData.featured.find(l => l.look_id === lookId)
            || discoverData.looks.find(l => l.look_id === lookId);
          if (look) setSavedLooks(prev => [look, ...prev]);
        } else {
          setSavedLooks(prev => prev.filter(l => l.look_id !== lookId));
        }
        toast.success(data.saved ? 'Look saved!' : 'Removed from saved');
      }
    } catch (e) { toast.error('Failed to save'); }
  };

  const themes = Object.keys(THEME_COLORS);

  return (
    <div className="min-h-screen pb-24 bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-lg md:max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Spotlight
            </h1>
            <div className="flex items-center gap-2">
              <button data-testid="search-btn" onClick={() => navigate('/search')} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <Search className="w-5 h-5 text-muted-foreground" />
              </button>
              <button data-testid="saved-looks-btn" onClick={() => setActiveTab('saved')} className={`p-2 rounded-full transition-colors ${activeTab === 'saved' ? 'bg-primary/10' : 'hover:bg-gray-100'}`}>
                {activeTab === 'saved' ? <BookmarkCheck className="w-5 h-5 text-primary" /> : <Bookmark className="w-5 h-5 text-muted-foreground" />}
              </button>
            </div>
          </div>
          {/* Sub-tabs */}
          <div className="flex gap-1 mt-3 bg-gray-100 rounded-xl p-1">
            <button
              data-testid="tab-looks"
              onClick={() => setActiveTab('looks')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                activeTab === 'looks' ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Looks
            </button>
            <button
              data-testid="tab-trending"
              onClick={() => setActiveTab('trending')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                activeTab === 'trending' ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Products
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-lg md:max-w-2xl mx-auto px-4 py-4">
        {activeTab === 'looks' ? (
          <div className="space-y-4">
            {/* Following / Discover Toggle */}
            <div className="flex gap-2">
              <button
                data-testid="mode-following"
                onClick={() => setLooksMode('following')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  looksMode === 'following'
                    ? 'bg-foreground text-background shadow-sm'
                    : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                }`}
              >
                Following
              </button>
              <button
                data-testid="mode-discover"
                onClick={() => setLooksMode('discover')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  looksMode === 'discover'
                    ? 'bg-foreground text-background shadow-sm'
                    : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                }`}
              >
                Discover
              </button>
            </div>

            {/* Theme Filter Pills */}
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              <button
                data-testid="theme-all"
                onClick={() => setSelectedTheme('')}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  !selectedTheme ? 'bg-primary text-white' : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                }`}
              >
                All Looks
              </button>
              {themes.map(t => (
                <button
                  key={t}
                  data-testid={`filter-${t.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => setSelectedTheme(selectedTheme === t ? '' : t)}
                  className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedTheme === t ? 'bg-primary text-white' : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Content based on mode */}
            {loading ? (
              <div className="text-center py-12">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : looksMode === 'following' ? (
              /* Following Mode */
              looks.length === 0 ? (
                <div className="text-center py-16 space-y-4">
                  <Sparkles className="w-12 h-12 text-muted-foreground/40 mx-auto" />
                  <p className="text-muted-foreground">No looks yet{selectedTheme ? ` for "${selectedTheme}"` : ' from people you follow'}.</p>
                  <p className="text-sm text-muted-foreground">Follow more people or switch to Discover!</p>
                  <button
                    data-testid="switch-to-discover"
                    onClick={() => setLooksMode('discover')}
                    className="px-5 py-2 bg-primary text-white rounded-full text-sm font-medium"
                  >
                    Explore Discover
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {looks.map(look => (
                    <LookGridItem
                      key={look.look_id}
                      look={look}
                      currentUser={currentUser}
                      isSaved={savedLookIds.has(look.look_id)}
                      onTap={() => setSelectedLook(look)}
                    />
                  ))}
                </div>
              )
            ) : (
              /* Discover Mode */
              <DiscoverView
                discoverData={discoverData}
                currentUser={currentUser}
                savedLookIds={savedLookIds}
                onSelectLook={setSelectedLook}
                navigate={navigate}
                selectedTheme={selectedTheme}
              />
            )}
          </div>
        ) : activeTab === 'saved' ? (
          <div className="space-y-4">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Saved Looks</h2>
            {savedLooks.length === 0 ? (
              <div className="text-center py-16 space-y-4">
                <BookmarkCheck className="w-12 h-12 text-muted-foreground/40 mx-auto" />
                <p className="text-muted-foreground">No saved looks yet</p>
                <p className="text-sm text-muted-foreground">Save looks from people you follow for inspiration!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {savedLooks.map(look => (
                  <LookGridItem
                    key={look.look_id}
                    look={look}
                    currentUser={currentUser}
                    isSaved={true}
                    onTap={() => setSelectedLook(look)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <TrendingTab products={trendingProducts} navigate={navigate} />
        )}
      </div>

      {/* Look Detail Carousel Modal */}
      {selectedLook && (
        <LookCarouselModal
          look={selectedLook}
          currentUser={currentUser}
          isSaved={savedLookIds.has(selectedLook.look_id)}
          onClose={() => setSelectedLook(null)}
          onLike={() => handleLike(selectedLook.look_id)}
          onSave={() => handleSave(selectedLook.look_id)}
          navigate={navigate}
        />
      )}
    </div>
  );
}

/* Grid item for looks — compact tile with overlay info */
function LookGridItem({ look, currentUser, isSaved, onTap }) {
  const isBeforeAfter = look.theme === 'Before & After';
  const gradientColor = THEME_COLORS[look.theme] || 'from-gray-300 to-gray-500';
  const firstImage = isBeforeAfter ? look.after_image : look.images?.[0];
  const hasVideo = !!look.video_url;
  const isLiked = look.likes?.includes(currentUser?.user_id);

  return (
    <button
      data-testid={`look-${look.look_id}`}
      onClick={onTap}
      className="relative aspect-[3/4] md:aspect-square rounded-xl overflow-hidden group bg-gray-100"
    >
      {hasVideo && !firstImage ? (
        <video src={look.video_url} muted preload="metadata" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
      ) : firstImage ? (
        <img src={firstImage} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-200">
          <Sparkles className="w-8 h-8 text-gray-400" />
        </div>
      )}

      {/* Theme badge */}
      <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white bg-gradient-to-r ${gradientColor} shadow-sm`}>
        {look.sub_theme || look.theme}
      </span>

      {/* Save indicator */}
      {isSaved && (
        <div className="absolute top-2 right-2">
          <BookmarkCheck className="w-4 h-4 text-white drop-shadow-md" />
        </div>
      )}

      {/* Bottom overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-2.5 pt-8">
        <div className="flex items-center gap-1.5 mb-1">
          {look.user_picture ? (
            <img src={look.user_picture} alt="" className="w-5 h-5 rounded-full object-cover border border-white/50" />
          ) : (
            <div className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-white text-[9px] font-bold">
              {look.user_name?.charAt(0) || 'U'}
            </div>
          )}
          <span className="text-white text-xs font-medium truncate">@{look.username}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-white/90 text-xs">
            <Heart className={`w-3 h-3 ${isLiked ? 'fill-red-400 text-red-400' : ''}`} />
            {look.likes?.length || 0}
          </span>
          {look.checklist && (
            <span className="flex items-center gap-1 text-white/90 text-xs">
              <ExternalLink className="w-3 h-3" />
              Checklist
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

/* Discover view with Featured Creators row + main engagement-ranked grid */
function DiscoverView({ discoverData, currentUser, savedLookIds, onSelectLook, navigate, selectedTheme }) {
  const { featured, looks: mainLooks } = discoverData;
  const hasContent = featured.length > 0 || mainLooks.length > 0;

  if (!hasContent) {
    return (
      <div className="text-center py-16 space-y-4">
        <Sparkles className="w-12 h-12 text-muted-foreground/40 mx-auto" />
        <p className="text-muted-foreground">
          {selectedTheme ? `No looks to discover for "${selectedTheme}"` : 'No new looks to discover right now'}
        </p>
        <p className="text-sm text-muted-foreground">Check back later as more creators share their looks!</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Featured Creators Row */}
      {featured.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Featured Creators</h3>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {featured.map(look => {
              const isBA = look.theme === 'Before & After';
              const img = isBA ? look.after_image : look.images?.[0];
              const gradientColor = THEME_COLORS[look.theme] || 'from-gray-300 to-gray-500';
              return (
                <button
                  key={look.look_id}
                  data-testid={`featured-look-${look.look_id}`}
                  onClick={() => onSelectLook(look)}
                  className="shrink-0 w-36 rounded-xl overflow-hidden group relative"
                >
                  <div className="aspect-[3/4] relative">
                    {img ? (
                      <img src={img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <span className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold text-white bg-gradient-to-r ${gradientColor}`}>
                      {look.sub_theme || look.theme}
                    </span>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-6">
                      <div className="flex items-center gap-1">
                        {look.user_picture ? (
                          <img src={look.user_picture} alt="" className="w-4 h-4 rounded-full object-cover border border-amber-400" />
                        ) : (
                          <div className="w-4 h-4 rounded-full bg-amber-400 flex items-center justify-center text-[8px] text-white font-bold">
                            {look.user_name?.charAt(0)}
                          </div>
                        )}
                        <span className="text-white text-[10px] font-medium truncate">@{look.username}</span>
                        <Sparkles className="w-2.5 h-2.5 text-amber-400 shrink-0" />
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Discover Grid */}
      {mainLooks.length > 0 && (
        <div>
          {featured.length > 0 && (
            <h3 className="text-sm font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Trending Looks</h3>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {mainLooks.map(look => (
              <LookGridItem
                key={look.look_id}
                look={look}
                currentUser={currentUser}
                isSaved={savedLookIds.has(look.look_id)}
                onTap={() => onSelectLook(look)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* Carousel modal: Slide 1 = Look image + actions, Slide 2 = Checklist */
function LookCarouselModal({ look, currentUser, isSaved, onClose, onLike, onSave, navigate }) {
  const isLiked = look.likes?.includes(currentUser?.user_id);
  const isBeforeAfter = look.theme === 'Before & After';
  const [activeSlide, setActiveSlide] = useState(0);
  const [carouselApi, setCarouselApi] = useState(null);

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setActiveSlide(carouselApi.selectedScrollSnap());
    carouselApi.on('select', onSelect);
    return () => carouselApi.off('select', onSelect);
  }, [carouselApi]);

  const hasChecklist = !!look.checklist;

  return (
    <div className="fixed inset-0 bg-black/85 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-lg md:max-w-xl rounded-2xl overflow-hidden max-h-[90vh] md:max-h-[85vh] flex flex-col mx-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <button
            onClick={() => { onClose(); navigate(`/profile/${look.user_id}`); }}
            className="flex items-center gap-2 hover:opacity-80"
          >
            {look.user_picture ? (
              <img src={look.user_picture} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-xs font-bold">
                {look.user_name?.charAt(0) || 'U'}
              </div>
            )}
            <div className="text-left">
              <span className="font-semibold text-sm block">@{look.username}</span>
              <span className="text-xs text-muted-foreground">{look.sub_theme ? `${look.theme} · ${look.sub_theme}` : look.theme}</span>
            </div>
          </button>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="close-look-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Slide indicators */}
        {hasChecklist && (
          <div className="flex justify-center gap-1.5 py-2 shrink-0">
            <button
              data-testid="slide-indicator-0"
              onClick={() => carouselApi?.scrollTo(0)}
              className={`h-1.5 rounded-full transition-all ${activeSlide === 0 ? 'w-6 bg-primary' : 'w-1.5 bg-gray-300'}`}
            />
            <button
              data-testid="slide-indicator-1"
              onClick={() => carouselApi?.scrollTo(1)}
              className={`h-1.5 rounded-full transition-all ${activeSlide === 1 ? 'w-6 bg-primary' : 'w-1.5 bg-gray-300'}`}
            />
          </div>
        )}

        {/* Carousel */}
        <div className="flex-1 overflow-hidden">
          <Carousel setApi={setCarouselApi} className="w-full">
            <CarouselContent className="ml-0">
              {/* Slide 1: Image */}
              <CarouselItem className="pl-0">
                <div className="overflow-y-auto max-h-[calc(90vh-140px)] md:max-h-[calc(85vh-140px)]">
                  {isBeforeAfter ? (
                    <div className="grid grid-cols-2 gap-0.5">
                      <div className="relative">
                        <img src={look.before_image} alt="Before" className="w-full aspect-square object-cover" />
                        <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full">Before</span>
                      </div>
                      <div className="relative">
                        <img src={look.after_image} alt="After" className="w-full aspect-square object-cover" />
                        <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full">After</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex overflow-x-auto snap-x snap-mandatory">
                      {look.video_url && (
                        <div className="w-full shrink-0 snap-center">
                          <VideoPlayer src={look.video_url} autoPlay className="w-full aspect-[4/5] md:aspect-square" />
                        </div>
                      )}
                      {look.images?.map((img, i) => (
                        <img key={img} src={img} alt={`Look ${i + 1}`} className="w-full shrink-0 snap-center aspect-[4/5] md:aspect-square object-cover" />
                      ))}
                    </div>
                  )}

                  {/* Actions + Caption */}
                  <div className="p-4 space-y-3">
                    <div className="flex items-center gap-4">
                      <button data-testid="modal-like-btn" onClick={onLike} className="flex items-center gap-1.5 hover:opacity-70 transition-opacity">
                        <Heart className={`w-6 h-6 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
                        <span className="font-medium">{look.likes?.length || 0}</span>
                      </button>
                      <button data-testid="modal-save-btn" onClick={onSave} className="ml-auto hover:opacity-70 transition-opacity">
                        <Bookmark className={`w-6 h-6 ${isSaved ? 'fill-primary text-primary' : ''}`} />
                      </button>
                    </div>
                    {look.caption && <p className="text-sm">{look.caption}</p>}
                    {hasChecklist && (
                      <button
                        data-testid="swipe-to-checklist-btn"
                        onClick={() => carouselApi?.scrollTo(1)}
                        className="w-full py-2.5 rounded-xl bg-primary/10 text-primary text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary/15 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Shop the Look — Swipe to see checklist
                      </button>
                    )}
                  </div>
                </div>
              </CarouselItem>

              {/* Slide 2: Checklist (only if look has a checklist) */}
              {hasChecklist && (
                <CarouselItem className="pl-0">
                  <div className="overflow-y-auto p-4 max-h-[calc(90vh-140px)] md:max-h-[calc(85vh-140px)]">
                    <div className="flex items-center gap-2 mb-4">
                      <ExternalLink className="w-5 h-5 text-primary" />
                      <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Shop the Look</h3>
                    </div>
                    <ChecklistView checklist={look.checklist} />
                    <button
                      data-testid="back-to-look-btn"
                      onClick={() => carouselApi?.scrollTo(0)}
                      className="w-full mt-4 py-2.5 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-gray-50 transition-colors"
                    >
                      Back to Look
                    </button>
                  </div>
                </CarouselItem>
              )}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </div>
  );
}

function TrendingTab({ products, navigate }) {
  const [userChecklists, setUserChecklists] = useState([]);
  const [addingTo, setAddingTo] = useState(null);
  const [selectedChecklist, setSelectedChecklist] = useState('');

  useEffect(() => {
    fetchChecklists();
  }, []);

  const fetchChecklists = async () => {
    try {
      const response = await authFetch(`${API}/checklists`);
      if (response.ok) setUserChecklists(await response.json());
    } catch (e) { console.error('fetchChecklists error:', e); }
  };

  const appendUtm = (url, productId) => {
    if (!url) return '#';
    try {
      const u = new URL(url);
      u.searchParams.set('utm_source', 'gloverse');
      u.searchParams.set('utm_medium', 'trending');
      u.searchParams.set('utm_content', productId || '');
      return u.toString();
    } catch { return url; }
  };

  const trackTrendingClick = async (p) => {
    try {
      await authFetch(`${API}/analytics/track-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: p.product_id,
          product_name: p.product_name,
          brand: p.brand,
          purchase_url: p.purchase_url,
          source: 'trending'
        })
      });
    } catch { /* silent */ }
  };

  const handleAddToChecklist = async (productId) => {
    if (!selectedChecklist) { toast.error('Select a checklist first'); return; }
    try {
      const checklist = userChecklists.find(c => c.checklist_id === selectedChecklist);
      if (!checklist) return;
      if (checklist.products?.includes(productId)) {
        toast.error('Product already in this checklist');
        return;
      }
      if ((checklist.products?.length || 0) >= 7) {
        toast.error('Checklist already has 7 products (max)');
        return;
      }
      const updatedProducts = [...(checklist.products || []), productId];
      const response = await authFetch(`${API}/checklists/${selectedChecklist}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products: updatedProducts })
      });
      if (response.ok) {
        toast.success('Product added to checklist!');
        setAddingTo(null);
        setSelectedChecklist('');
        fetchChecklists();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to add');
      }
    } catch (e) {
      toast.error('Failed to add product');
    }
  };

  const defaultImg = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'%3E%3Crect width='48' height='48' rx='8' fill='%23f3e8ff'/%3E%3Ctext x='50%25' y='55%25' font-size='20' text-anchor='middle' dominant-baseline='middle' fill='%23a855f7'%3EG%3C/text%3E%3C/svg%3E";

  return (
    <div className="space-y-4">
      <WeeklyPicksCard />
      {products.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">No trending products yet. Start adding products to checklists!</p>
      ) : (
        products.filter(p => p.purchase_url).map((p, i) => (
          <div key={p.product_id || i} className="bg-white rounded-xl border border-border overflow-hidden">
            <div className="flex items-center gap-3 p-3">
              <span className="text-lg font-bold text-primary/60 w-7 text-center shrink-0">#{i + 1}</span>
              <img
                src={p.image || defaultImg}
                alt={p.product_name}
                className="w-12 h-12 rounded-lg object-cover shrink-0"
                onError={(e) => { e.target.src = defaultImg; }}
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{p.product_name}</p>
                <p className="text-xs text-muted-foreground">{p.brand} &middot; {p.product_type?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</p>
                {p.price && <p className="text-sm font-bold text-primary mt-0.5">{p.price}</p>}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="text-xs font-medium text-muted-foreground bg-gray-100 px-2 py-0.5 rounded-full">{p.usage_count} uses</span>
                <button
                  data-testid={`add-to-checklist-${p.product_id}`}
                  onClick={() => setAddingTo(addingTo === p.product_id ? null : p.product_id)}
                  className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                  title="Add to checklist"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
            {addingTo === p.product_id && (
              <div className="px-3 pb-3 border-t border-border pt-2 flex items-center gap-2">
                <div className="relative flex-1">
                  <select
                    value={selectedChecklist}
                    onChange={(e) => setSelectedChecklist(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-white text-sm appearance-none pr-8"
                    data-testid={`checklist-select-${p.product_id}`}
                  >
                    <option value="">Select checklist</option>
                    {userChecklists.map(c => (
                      <option key={c.checklist_id} value={c.checklist_id}>
                        {c.name} ({c.products?.length || 0}/7)
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                </div>
                <button
                  data-testid={`confirm-add-${p.product_id}`}
                  onClick={() => handleAddToChecklist(p.product_id)}
                  disabled={!selectedChecklist}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-40 hover:bg-primary/90 transition-colors"
                >
                  Add
                </button>
                <button onClick={() => { setAddingTo(null); setSelectedChecklist(''); }} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            )}
            {p.purchase_url && (
              <a
                href={appendUtm(p.purchase_url, p.product_id)}
                onClick={(e) => { e.preventDefault(); trackTrendingClick(p); window.open(appendUtm(p.purchase_url, p.product_id), '_blank', 'noopener,noreferrer'); }}
                data-testid={`buy-now-${p.product_id}`}
                className="block px-3 pb-2"
              >
                <span className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Buy now
                </span>
              </a>
            )}
          </div>
        ))
      )}
    </div>
  );
}
